/**
 * The guest owner key (save + share, contract 1.11.0; OMAR, 10-22).
 *
 * A guest gets an owner key the first time they SAVE (never on page boot): 256
 * bits, shown once by `POST /v1/engine/owners`, kept in this browser and sent as
 * `Authorization: Bearer <key>`. It is a password for every design saved under it.
 * Sign-in (ID1) later attaches it to the Shopify customer.
 *
 * ⚠ Safari deletes script-written storage after 7 days without a visit, so the key
 * alone is not durable. The customer's way back is the RECOVERY link
 * (`/k#<recoverySecret>`, a separate secret that the server rotates), never the
 * owner key in a link.
 *
 * Every storage read and write is guarded: blocked storage means "no owner yet".
 */

const STORE_KEY = 'tf.owner';

/**
 * The fragment secrets (contract 1.11.0, the OpenAPI patterns): a share secret is a bare
 * 256-bit base64url value (43 characters); a recovery secret carries the `rtr_` prefix
 * (OMAR M6: secret scanners can spot one leaked into a paste, a log or a repo; recovery
 * links get bookmarked and emailed, so they are the likeliest to leak).
 */
const SHARE_SECRET = /^[A-Za-z0-9_-]{43}$/;
const RECOVERY_SECRET = /^rtr_[A-Za-z0-9_-]{43}$/;

export interface StoredOwner {
    ownerId: string;
    /** Opaque (`rto_…`); never parsed, never logged, never put in a URL. */
    ownerKey: string;
    /** When this device got the key, for the "keep your link" reminder. */
    since: string;
    /** When the customer last made a recovery link on this device; null = never. */
    recoveryMadeAt: string | null;
    /** 1.26.0: this owner is tied to the customer's LITKO account (keep designs, K5); absent = a guest. */
    linked?: boolean;
}

export function readOwner(): StoredOwner | null {
    try {
        const raw = window.localStorage.getItem(STORE_KEY);
        if (raw === null) {
            return null;
        }
        const o = JSON.parse(raw) as Partial<StoredOwner>;
        if (typeof o.ownerId !== 'string' || typeof o.ownerKey !== 'string' || o.ownerKey === '') {
            return null;
        }
        return { ownerId: o.ownerId, ownerKey: o.ownerKey, since: o.since ?? '', recoveryMadeAt: o.recoveryMadeAt ?? null, linked: o.linked === true };
    } catch {
        return null;
    }
}

export function writeOwner(owner: StoredOwner): boolean {
    try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(owner));
        return true;
    } catch {
        return false;
    }
}

export function forgetOwner(): void {
    try {
        window.localStorage.removeItem(STORE_KEY);
    } catch {
        // Nothing to do.
    }
}

/** The share page: `/s#<shareSecret>`. The recovery page: `/k#<recoverySecret>`. */
export type SecretPage = { kind: 'share'; secret: string | null } | { kind: 'recover'; secret: string | null } | { kind: 'other' };

export function secretPage(pathname: string, hash: string): SecretPage {
    if (/^\/s\/?$/.test(pathname)) {
        return { kind: 'share', secret: secretFromFragment(hash, SHARE_SECRET) };
    }
    if (/^\/k\/?$/.test(pathname)) {
        return { kind: 'recover', secret: secretFromFragment(hash, RECOVERY_SECRET) };
    }
    return { kind: 'other' };
}

export function secretFromFragment(hash: string, pattern: RegExp = SHARE_SECRET): string | null {
    const raw = hash.startsWith('#') ? hash.slice(1) : hash;
    return pattern.test(raw) ? raw : null;
}

/**
 * Take the secret out of the address bar once it is read (OMAR): it then can't
 * be copied by accident from the address bar, synced into another device's
 * history, or caught in a screenshot. The page keeps it in memory only.
 */
export function stripFragment(): void {
    try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch {
        // An old browser keeps the fragment; the page still works.
    }
}

export function shareUrl(origin: string, shareSecret: string): string {
    return `${origin}/s#${shareSecret}`;
}

export function recoveryUrl(origin: string, recoverySecret: string): string {
    return `${origin}/k#${recoverySecret}`;
}

/**
 * Compare two ETags as the server does: Cloudflare may weaken a strong tag to
 * `W/"…"` on the way out (PRIYA 1), and the value inside is what matters.
 */
export function sameEtag(a: string | null, b: string | null): boolean {
    return a !== null && b !== null && bareEtag(a) === bareEtag(b);
}

export function bareEtag(etag: string): string {
    return etag.replace(/^W\//, '');
}
